import { cn } from '@/lib/utils';

/**
 * Gradient Style Utilities
 * Centralized gradient patterns for consistency across the app
 */

// LEGACY: blue gradients are banned by the brand system (ONE accent = gold).
// These names are kept as aliases that resolve to the gold CTA treatment so
// existing call sites (~45 files) keep compiling and render correctly. Do NOT
// use these in new code — use `goldGradient*` or the default `<Button>` directly.

/** @deprecated Use `goldGradient` or default `<Button>`. */
export const primaryGradient = cn('bg-active-gold');

/** @deprecated Use `text-active-gold` directly. */
export const primaryGradientText = cn('text-active-gold');

/** @deprecated Use `goldGradientHorizontal`. */
export const primaryGradientHorizontal = cn('bg-active-gold');

/** @deprecated Use `goldGradientActive` or default `<Button>`. */
export const primaryGradientActive = cn(
	'bg-active-gold text-black font-medium border-transparent',
	'hover:bg-active-gold/90 hover:text-black'
);

/** @deprecated Use `goldGradientOnHover`. */
export const primaryGradientOnHover = cn(
	'hover:bg-active-gold hover:text-black'
);

// Container background gradients
export const panelGradient = cn(
	'bg-gradient-to-br from-[hsl(var(--component-bg-mid))]',
	'via-[hsl(var(--component-bg-start))] to-[hsl(var(--component-bg-mid))]'
);

export const cardGradient = cn(
	'bg-gradient-to-br from-[hsl(var(--component-bg-end))]',
	'to-[hsl(var(--component-bg-subtle))]'
);

// TCGPlayer blue gradient (matches their brand)
export const tcgBlueGradient = cn(
	'bg-gradient-to-b from-[hsl(210,100%,48%)]',
	'via-[hsl(210,100%,42%)] to-[hsl(210,95%,35%)]'
);

export const tcgBlueGradientActive = cn(
	tcgBlueGradient,
	'border-[hsl(210,100%,40%)] text-white hover:opacity-80',
	'shadow-[0_2px_6px_hsl(210,100%,40%,0.2)]'
);

// eBay green gradient (matches their brand)
export const ebayGreenGradient = cn(
	'bg-gradient-to-b from-[hsl(145,65%,48%)]',
	'via-[hsl(145,65%,42%)] to-[hsl(145,60%,35%)]'
);

export const ebayGreenGradientActive = cn(
	ebayGreenGradient,
	'border-success text-white hover:opacity-80',
	'shadow-[0_2px_6px_hsl(145,65%,40%,0.2)]'
);

// eBay medium gray gradient (legacy)
export const ebayGrayGradient = cn(
	'bg-gradient-to-b from-[hsl(0,0%,48%)]',
	'via-[hsl(0,0%,45%)] to-[hsl(0,0%,40%)]'
);

export const ebayGrayGradientActive = cn(
	ebayGrayGradient,
	'border-border/40 text-foreground hover:opacity-80',
	'shadow-[0_2px_6px_hsl(0,0%,0%,0.15)]'
);

// eBay off-white gradient (subtle white with slight gradient)
export const ebayWhiteGradient = cn(
	'bg-gradient-to-b from-[hsl(0,0%,98%)]',
	'via-[hsl(0,0%,96%)] to-[hsl(0,0%,94%)]'
);

export const ebayWhiteGradientActive = cn(
	ebayWhiteGradient,
	'border-border text-foreground hover:opacity-80',
	'shadow-[0_2px_6px_hsl(0,0%,0%,0.08)]'
);

// Chrome surface gradient - subtle top-to-bottom lighting for headers/toolbars/sections
export const chromeSurfaceGradient = cn(
	'bg-gradient-to-b from-[hsl(220,10%,10%)] to-[hsl(220,10%,7%)]'
);

// Glass button style for use in glass modals/containers (unselected state)
export const glassButtonInactive = cn(
	'bg-black/20 border-white/[0.08]',
	'hover:border-white/20 hover:bg-white/[0.05]',
	'transition-colors duration-200'
);

// Chinese Red gradient (for secondary CTAs, alternative highlights)
export const chineseRedGradient = cn(
	'bg-gradient-to-b from-[hsl(355,90%,52%)]',
	'via-[hsl(355,90%,45%)] to-[hsl(355,95%,38%)]'
);

export const chineseRedGradientActive = cn(
	chineseRedGradient,
	'border-destructive text-white hover:text-white hover:opacity-80',
	'shadow-[0_2px_6px_hsl(355,90%,45%/0.3)]'
);

// Brand gradient (dark red theme)
export const brandGradient = cn(
	'bg-gradient-to-b from-[hsl(355,55%,32%)]',
	'via-[hsl(355,50%,25%)] to-[hsl(355,45%,20%)]'
);

export const brandGradientActive = cn(
	brandGradient,
	'border-destructive/50 text-white hover:opacity-80',
	'shadow-[0_2px_6px_hsl(355,55%,28%/0.3)]'
);

// ============================================
// BRAND GOLD CTA TREATMENT
// Single accent system: solid active-gold + black text, no gradient.
// Used by primary CTAs (Add Item, etc.). One brand color across the app.
// ============================================
export const goldGradient = cn('bg-active-gold');
export const goldGradientText = cn('text-active-gold');
export const goldGradientHorizontal = cn('bg-active-gold');
export const goldGradientActive = cn(
	'bg-active-gold text-black font-medium border-transparent',
	'hover:bg-active-gold/90 hover:text-black'
);
export const goldGradientOnHover = cn(
	'hover:bg-active-gold hover:text-black'
);

// Purple alias for Chinese Red (legacy naming)
/** @deprecated Use chineseRedGradient instead */
export const purpleGradient = chineseRedGradient;
/** @deprecated Use chineseRedGradientActive instead */
export const purpleGradientActive = chineseRedGradientActive;
